/* Wave Commerce customizations.
   Lives outside the theme's own JS (global.js, custom.js) so theme updates don't clobber it —
   same reason assets/wave.css sits next to base.css.
   Loaded as a module from snippets/scripts.liquid, after custom.js. */

import { $4, $$4 } from '@theme/utilities';

/* Sticky add-to-cart quantity picker — see snippets/sticky-add-to-cart.liquid.
   The +/- interaction is the theme's native hdt-quantity-wrapp; this only exists to get the
   value into the cart request. The sticky bar renders at body level, outside the product form,
   and adds to cart by clicking that form's submit button (assets/sticky-bar.js), so its
   quantity is never part of the FormData unless we mirror it across first. */
class StickyQuantity extends HTMLElement {
  #controller;
  #syncing = false;
  connectedCallback() {
    this.input = $4('input[type="number"]', this);
    if (!this.input) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.input.addEventListener('change', () => this.#sync(), { signal });
    // capture phase so the form is up to date before sticky-bar.js clicks the submit button
    $4('[ref="variantButton"]', this.closest('.hdt-sticky-add-to-cart'))
      ?.addEventListener('click', () => this.#sync(), { signal, capture: true });
    // quantity rules can differ per variant
    this.#form?.addEventListener('product:updateInfo', this.#updateRules, { signal });
  }
  disconnectedCallback() {
    this.#controller?.abort();
  }
  get #form() {
    const id = $4('[tmp-sticky-atc]')?.dataset.form;
    return id ? document.forms[id] : null;
  }
  get #formInput() {
    const form = this.#form;
    if (!form) return null;
    let input = $4('input[name="quantity"]', form);
    if (!input) {
      input = Object.assign(document.createElement('input'), { type: 'hidden', name: 'quantity' });
      form.appendChild(input);
    }
    return input;
  }
  #sync() {
    if (this.#syncing) return;
    const input = this.#formInput;
    if (!input || input.value === this.input.value) return;
    this.#syncing = true;
    input.value = this.input.value;
    // lets hdt-quantity-wrapp re-validate against the variant's rules
    input.dispatchEvent(new Event('change', { bubbles: true }));
    // and mirror back whatever it clamped to
    if (input.value !== this.input.value) {
      this.input.value = input.value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#syncing = false;
  }
  #updateRules = (event) => {
    const { sectionId, html } = event.detail,
    domFragment = $4(`#shopify-section-${sectionId}`, document.createRange().createContextualFragment(html)),
    template = $4('template[tmp-sticky-atc]', domFragment);
    if (!template) return;
    const newHost = $4('.hdt-sticky-add-to-cart__quantity', template.content),
    newInput = newHost && $4('input[type="number"]', newHost);
    if (!newInput) return;
    ['min', 'max', 'step', 'data-min'].forEach((attr) => {
      const value = newInput.getAttribute(attr);
      value === null ? this.input.removeAttribute(attr) : this.input.setAttribute(attr, value);
    });
    $4('hdt-quantity-wrapp', this)?.setAttribute('data-variant-id', $4('hdt-quantity-wrapp', newHost)?.dataset.variantId || '');
    this.toggleAttribute('disabled', newHost.hasAttribute('disabled'));
    this.#setDisabled(newInput.disabled);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  #setDisabled(disabled) {
    this.input.disabled = disabled;
    $$4('button', this).forEach((button) => (button.disabled = disabled));
  }
}
customElements.define('hdt-sticky-quantity', StickyQuantity);
